'use client';

import { cn } from '@core/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, useEffect, useMemo, useRef } from 'react';

import type { HTMLTagProps } from '../../../types';

const textAreaVariants = cva(
  'border-grayscale-300 has-[textarea:focus]:border-grayscale-900 flex items-center justify-center border-1 bg-Gray-white px-16 transition-colors hide-scrollbar',
  {
    variants: {
      dimensions: {
        //! 텍스트 라인 24px 기준 py = (h - 24)/2
        H40: 'h-40 py-8',
        H48: 'h-48 py-12',
        H56: 'h-56 py-16',
      },
      roundness: {
        rectangle: 'rounded-xsmall',
        capsule: 'rounded-pill',
      },
    },

    defaultVariants: {
      dimensions: 'H48',
      roundness: 'rectangle',
    },
  },
);

interface TextAreaProps
  extends HTMLTagProps<'textarea'>, VariantProps<typeof textAreaVariants> {
  autoResize?: boolean;
  textareaClassName?: string;
  maxLines?: number;
  leftContent?: React.ReactNode;
  rightContent?: React.ReactNode;
}

export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(
  (
    {
      autoResize = true,
      dimensions = 'H48',
      className,
      roundness,
      textareaClassName,
      leftContent,
      rightContent,
      maxLines,
      rows,
      onChange,
      value,
      ...restTextareaProps
    }: TextAreaProps,
    ref,
  ) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const _textareaRef = useRef<HTMLTextAreaElement>(null);
    const textareaRef = (ref ?? _textareaRef) as typeof _textareaRef;

    const handleDynamicHeight = useMemo(() => {
      if (!autoResize) return null;

      return () => {
        if (!textareaRef.current || !containerRef.current) return;

        const textareaStyle = textareaRef.current.style;

        //! auto로 되돌려 rows 기준 네이티브 최소 높이로 복귀시킨 뒤
        //! 콘텐츠 높이(scrollHeight)만큼 확장
        textareaStyle.height = 'auto';
        textareaStyle.height = `${textareaRef.current.scrollHeight}px`;

        if (maxLines) {
          textareaStyle.maxHeight = `${maxLines}lh`;
          textareaStyle.overflowY = 'auto';
        }

        containerRef.current.style.height = 'fit-content';
      };
    }, [autoResize, maxLines, textareaRef]);

    useEffect(() => {
      //! 마운트 시 최소 높이 보장 + 외부 state를 통한 value 변경에 대한 처리
      handleDynamicHeight?.();
    }, [handleDynamicHeight, value]);

    return (
      <div
        ref={containerRef}
        className={cn(
          textAreaVariants({
            dimensions,
            roundness,
          }),
          className,
        )}
      >
        {leftContent}
        <textarea
          {...restTextareaProps}
          ref={textareaRef}
          //! 브라우저 기본 rows(2)가 아닌 1줄을 기본 최소 높이로 사용 (Input과 동일한 단일 라인)
          rows={rows ?? 1}
          className={cn(
            'h-full flex-1 resize-none overflow-hidden bg-transparent outline-none hide-scrollbar typo-inherit placeholder:text-Gray-500',
            textareaClassName,
          )}
          value={value}
          onChange={(e) => {
            onChange?.(e);

            if (value === undefined) {
              handleDynamicHeight?.();
            }
          }}
        />
        {rightContent}
      </div>
    );
  },
);

TextArea.displayName = 'TextArea';
